import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Loader2,
  Download,
  LogOut,
  MapPin,
  CalendarCheck,
  UserCheck,
  Share2,
} from "lucide-react";
import { usePortalAuth } from "../lib/portalAuth";
import { OW_API, CATEGORY_TITLE, scoreColor } from "../lib/operatorWalk";
import CategoryBars from "../components/ow/CategoryBars";
import TrendCharts from "../components/ow/TrendCharts";
import ShareModal from "../components/ow/ShareModal";
import SharedLinksPanel from "../components/ow/SharedLinksPanel";
import Tm from "../components/ow/Tm";

function Panel({ title, children, testid, action }) {
  return (
    <section className="bg-white border border-brand-200" data-testid={testid}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-brand-100">
        <h2 className="font-serif text-2xl text-brand-700">{title}</h2>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

export default function PortalDashboard() {
  const { user, logout, authHeader } = usePortalAuth();
  const [company, setCompany] = useState("");
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState("");
  const [shareReport, setShareReport] = useState(null);
  const [sharesRefresh, setSharesRefresh] = useState(0);

  // Load locations once
  useEffect(() => {
    axios
      .get(`${OW_API}/portal/locations`, { headers: authHeader })
      .then((res) => {
        setCompany(res.data.client?.company_name || user?.name || "");
        const locs = res.data.locations || [];
        setLocations(locs);
        if (locs.length) setLocationId(locs[0].id);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLocationData = useCallback(
    async (locId) => {
      setLoading(true);
      try {
        const [ov, tr, rp] = await Promise.all([
          axios.get(`${OW_API}/portal/overview?location_id=${locId}`, { headers: authHeader }),
          axios.get(`${OW_API}/portal/trends?location_id=${locId}`, { headers: authHeader }),
          axios.get(`${OW_API}/portal/reports?location_id=${locId}`, { headers: authHeader }),
        ]);
        setOverview(ov.data);
        setTrends(tr.data);
        setReports(rp.data.reports || []);
      } catch {
        setOverview(null);
      } finally {
        setLoading(false);
      }
    },
    [authHeader]
  );

  useEffect(() => {
    if (locationId) loadLocationData(locationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const downloadPdf = async (reportId) => {
    setDownloading(reportId);
    try {
      const res = await axios.get(`${OW_API}/portal/reports/${reportId}/pdf`, {
        headers: authHeader,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `operator-walk-${reportId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      /* swallow; button resets */
    } finally {
      setDownloading("");
    }
  };

  const current = overview?.current;

  return (
    <div className="min-h-screen bg-brand-100" data-testid="page-portal-dashboard">
      {/* TOP BAR */}
      <header className="bg-brand-700 text-white sticky top-0 z-40" data-testid="portal-topbar">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-white text-brand-700 flex items-center justify-center font-serif text-lg leading-none shrink-0">
              H
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-[10px] tracking-[0.25em] uppercase text-brand-300">The Operator Walk<Tm /></div>
              <div className="font-serif text-lg truncate" data-testid="portal-company-name">
                {company || "Your dashboard"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {locations.length > 1 && (
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                className="bg-brand-800 text-white text-sm border border-brand-600 px-3 py-2 focus:outline-none"
                data-testid="portal-location-select"
              >
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            )}
            {locations.length === 1 && (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-sm text-brand-100" data-testid="portal-single-location">
                <MapPin className="w-4 h-4 text-brand-300" /> {locations[0].name}
              </span>
            )}
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm text-brand-100 hover:text-white transition-colors"
              data-testid="portal-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10 space-y-8">
        {loading ? (
          <div className="py-32 flex items-center justify-center text-ink-muted" data-testid="portal-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your dashboard
          </div>
        ) : locations.length === 0 ? (
          <div className="bg-white border border-brand-200 p-12 text-center" data-testid="portal-no-locations">
            <p className="font-serif text-2xl text-brand-700">No locations yet</p>
            <p className="mt-3 text-ink-soft">Your account is being set up. Please check back shortly.</p>
          </div>
        ) : !overview?.has_data ? (
          <div className="bg-white border border-brand-200 p-12 text-center" data-testid="portal-empty-state">
            <CalendarCheck className="w-12 h-12 text-brand-400 mx-auto" />
            <p className="mt-5 font-serif text-2xl text-brand-700 max-w-lg mx-auto leading-snug">
              Your first Operator Walk is scheduled.
            </p>
            <p className="mt-3 text-ink-soft max-w-md mx-auto">
              Your monthly report will appear here within 72 hours of the visit.
            </p>
          </div>
        ) : (
          <>
            {/* CURRENT MONTH */}
            <Panel
              title="Current Month"
              testid="portal-current-panel"
              action={
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShareReport(current)}
                    className="inline-flex items-center gap-2 border border-brand-300 text-brand-700 hover:bg-brand-100 px-4 py-2.5 text-sm font-medium transition-colors"
                    data-testid="portal-share-current-btn"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                  <button
                    onClick={() => downloadPdf(current.id)}
                    disabled={downloading === current.id}
                    className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                    data-testid="portal-download-current-btn"
                  >
                    {downloading === current.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Download report
                  </button>
                </div>
              }
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-brand-400 mb-2">
                    {current.month_label}
                  </div>
                  <div className="flex items-end gap-2">
                    <span
                      className="font-serif text-7xl leading-none"
                      style={{ color: scoreColor(current.percentage) }}
                      data-testid="portal-current-score"
                    >
                      {current.total}
                    </span>
                    <span className="font-serif text-2xl text-ink-muted mb-1">/100</span>
                  </div>
                  <div className="mt-2 text-ink-soft">{current.percentage}% overall score</div>
                  {current.visit_date && (
                    <div className="mt-5 inline-flex items-center gap-2 text-sm text-ink-muted">
                      <CalendarCheck className="w-4 h-4 text-brand-400" /> Most recent visit: {current.visit_date}
                    </div>
                  )}
                  {current.conducted_by && (
                    <div className={`${current.visit_date ? "mt-2" : "mt-5"} flex items-center gap-2 text-sm text-ink-muted`} data-testid="portal-conducted-by">
                      <UserCheck className="w-4 h-4 text-brand-400" /> Conducted by: {current.conducted_by}
                    </div>
                  )}
                </div>
                <div className="lg:col-span-8">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-brand-400 mb-3">
                    Score by category
                  </div>
                  <CategoryBars categories={current.categories} />
                </div>
              </div>
            </Panel>

            {/* TOP 3 OPPORTUNITIES */}
            <Panel title="Top 3 Opportunities" testid="portal-opportunities-panel">
              {current.opportunities?.length ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-100 border border-brand-100">
                  {current.opportunities.map((o, i) => (
                    <div key={i} className="bg-white p-5" data-testid={`portal-opportunity-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-serif text-2xl text-brand-300">{i + 1}</span>
                        <span className="text-[10px] tracking-[0.18em] uppercase text-brand-500">
                          {CATEGORY_TITLE[o.category] || o.category}
                        </span>
                      </div>
                      <h3 className="font-serif text-xl text-brand-700 leading-snug">{o.title}</h3>
                      {o.description && <p className="mt-2 text-sm text-ink-soft leading-relaxed">{o.description}</p>}
                      {o.action && (
                        <p className="mt-3 text-sm text-ink-soft leading-relaxed">
                          <span className="font-medium text-brand-700">Recommended action: </span>
                          {o.action}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-ink-muted text-sm">No opportunities recorded for this month.</p>
              )}
            </Panel>

            {/* TRENDS */}
            <Panel title="Trends" testid="portal-trends-panel">
              <TrendCharts series={trends?.series || []} categories={trends?.categories || []} />
            </Panel>

            {/* PAST REPORTS */}
            <Panel title="Past Reports" testid="portal-reports-panel">
              {reports.length ? (
                <div className="divide-y divide-brand-100" data-testid="portal-reports-list">
                  {reports.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between py-4 gap-4"
                      data-testid={`portal-report-row-${r.id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <span
                          className="font-serif text-2xl shrink-0"
                          style={{ color: scoreColor(r.percentage) }}
                        >
                          {r.total}
                        </span>
                        <div className="min-w-0">
                          <div className="text-brand-700 font-medium truncate">{r.month_label}</div>
                          <div className="text-xs text-ink-muted">{r.percentage}% overall</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => setShareReport(r)}
                          className="inline-flex items-center gap-2 border border-brand-200 text-brand-700 hover:bg-brand-100 px-3 py-2 text-sm font-medium transition-colors"
                          data-testid={`portal-report-share-${r.id}`}
                        >
                          <Share2 className="w-4 h-4" /> Share
                        </button>
                        <button
                          onClick={() => downloadPdf(r.id)}
                          disabled={downloading === r.id}
                          className="inline-flex items-center gap-2 border border-brand-200 text-brand-700 hover:bg-brand-100 px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
                          data-testid={`portal-report-download-${r.id}`}
                        >
                          {downloading === r.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-ink-muted text-sm">No past reports yet.</p>
              )}
            </Panel>

            {/* SHARED LINKS */}
            <SharedLinksPanel refreshKey={sharesRefresh} />
          </>
        )}

        <div className="text-center text-xs text-ink-muted pt-4">
          Holder Hospitality Consulting.{" "}
          <Link to="/" className="underline hover:text-brand-700">
            holderhospitality.consulting
          </Link>
        </div>
      </main>

      {shareReport && (
        <ShareModal
          report={shareReport}
          onClose={() => setShareReport(null)}
          onCreated={() => setSharesRefresh((k) => k + 1)}
        />
      )}
    </div>
  );
}
